import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Config,
  Logger,
  LogLevel,
  PixelStreaming,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import useAsyncEffect from 'use-async-effect';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '../../../providers/MytaverseEventProvider/context';
import PixelStreamingLogsService from '@services/PixelStreamingLogsService';
import MytaStreamsService, {
  IMytaLease,
  ILeasePhase,
  NoFreeSlotError,
} from '@services/MytaStreamsService';
import { MytaverseLogger } from '@helpers/logger';
import { env } from 'env';
import { IEvent, IPixelStreamingClient } from '@interfaces/event';
import { IInitMessage } from '@interfaces/streaming';
import { IMytaStreamsContext, MytaLeaseStatus } from './interfaces';

// Retry ceiling is deliberately below the server's largest retryAfterSec hints (capacity_wait sends
// 60): at our user counts the extra polls are free, and a cold node that turns ready between hints
// should be seated within ~15 s, not a minute later.
// The floor exists so a hint of 0/1 cannot spin; it is NOT a policy knob. The server decides cadence
// per phase (3 s once a slot is nearly seatable, 5 s while a node boots, 20-30 s during a capacity
// drought), and a 5 s floor here silently discarded the fast end of that — a measured entry lost ~6 s
// sitting between two polls while its slot was already seatable.
const LEASE_RETRY_DELAY_MS = 10000;
const LEASE_RETRY_MIN_DELAY_MS = 2500;
const LEASE_RETRY_MAX_DELAY_MS = 15000;
const LEASE_RENEW_INTERVAL_MS = 45000;

// KI-37 media watchdog. The failure it exists for, measured 2026-07-29 (run
// hubcamp-20260729-105152-c1-warm-ohio, u2): the lease is granted, the signalling socket opens and NEVER
// closes or errors, and then nothing happens — no offer, no data channel, no INIT, no frame — while this
// provider renews the lease every 45 s forever and the UI sits on "Loading your experience". With
// `WaitForStreamer: true` the frontend waits for its `StreamerId` to appear in the streamer list, and if that
// streamer is not registered at that moment (a slot whose UE restarted — KI-2 login-map idle-quit — or one
// that has not finished registering) the wait is silent and unbounded. The server sees a valid seat and
// healthy renewals, so nothing reclaims it: only the client can notice that no media ever arrived.
//
// 15 s for the first frame: a warm seat frames in ~0.6 s and the slowest healthy path measured this month is
// ~9 s, so 15 s cannot false-positive on a working stream while still being far inside the ~20 s window in
// which the hub's own backend teardown (KI-36) would otherwise strand the user.
const FIRST_FRAME_TIMEOUT_MS = 15000;
// Mid-session freeze (KI-20 collateral: a neighbour slot's GPU fault froze a healthy slot's encoder). Frames
// stop while everything else stays nominal, so `framesDecoded` going flat is the only honest signal.
const MEDIA_STALL_TIMEOUT_MS = 10000;
// Two rebuilds on the same lease, then give the slot back. A third attempt on a slot that has failed twice is
// worth less than the ladder's chance to move the user somewhere else (its tiers re-run from scratch).
const MAX_PEER_REBUILDS = 2;
// While the entry is still in flight, print one state line every this often. The operator asked to be able to
// watch an entry happen in real time — which node, which slot, which step, and how long each took — and the
// KI-37 hunt showed why: the failure IS silence, so a periodic line that says "still waiting, here is the
// state" is worth more than any post-mortem. It stops as soon as the first frame decodes.
const ENTRY_HEARTBEAT_MS = 5000;

interface IUseMytaStreamsSessionProps {
  enabled: boolean;
  currentEvent?: IEvent;
  pixelStreamingClient: IPixelStreamingClient | null;
  initMessageHandler: () => Promise<IInitMessage | null>;
  initMessageSended: boolean;
  setInitMessageSended: Dispatch<SetStateAction<boolean>>;
  setGameReadyToPlay: Dispatch<SetStateAction<boolean>>;
  gameSound: number;
  setMytaPixelStreaming: Dispatch<SetStateAction<PixelStreaming | null>>;
  // Needed for the reload rebind: the loader is gated on `teleportFinished`, which handleJoinedToEvent
  // resets to false on every entry and only a TELEPORT_FINISHED can set. After an F5 that event is in the
  // past — see adoptLease.
  setTeleportFinished: Dispatch<SetStateAction<boolean>>;
  // Reload rebind puts the participant back in the room the slot is actually in — see adoptLease. Takes the
  // slot's map, not a room: this provider knows which venue the seat is in, the event provider owns the
  // mapping to its own rooms and the participant state that `currentRoom` is derived from.
  restoreSeatedRoom: (mapName?: string) => void;
}

export const useMytaStreamsSession = ({
  enabled,
  currentEvent,
  pixelStreamingClient,
  initMessageHandler,
  initMessageSended,
  setInitMessageSended,
  setGameReadyToPlay,
  gameSound,
  setMytaPixelStreaming,
  setTeleportFinished,
  restoreSeatedRoom,
}: IUseMytaStreamsSessionProps): IMytaStreamsContext => {
  const { userId } = useMytaverse();

  const eventBuildUri = pixelStreamingClient?.clientParams?.buildUri;

  const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming | null>(
    null,
  );
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [dataChannelOpened, setDataChannelOpened] = useState(false);
  const [playStream, setPlayStream] = useState(false);
  const [mytaLoading, setMytaLoading] = useState(true);
  const [connectedSlot, setConnectedSlot] = useState('');
  const [lease, setLease] = useState<IMytaLease | null>(null);
  const [leaseStatus, setLeaseStatus] = useState<MytaLeaseStatus>('idle');
  const [leaseError, setLeaseError] = useState<string | null>(null);
  const [leasePhase, setLeasePhase] = useState<ILeasePhase | null>(null);
  const [target, setTarget] = useState<{
    signallingUrl: string;
    streamerId: string;
  } | null>(null);

  // KI-37: bumping this re-runs the [target] effect, whose cleanup disconnects the old instance before the
  // body builds a new one — the KI-22 one-peer-per-slot rule is satisfied by construction (close, then retry).
  const [peerRebuild, setPeerRebuild] = useState(0);
  // Re-runs the lease effect after we hand a bad slot back, so the ladder gets a fresh chance to place us.
  const [leaseAttempt, setLeaseAttempt] = useState(0);
  // Re-runs the INIT effect while the player-session mint comes back empty — SM recreates a dead game
  // session in the background, so the payload must be retried; one silent null used to strand the user
  // on the login map forever.
  const [initAttempt, setInitAttempt] = useState(0);
  const rebuildsRef = useRef(0);
  // Leases this component already released on purpose. The [lease] effect's cleanup also releases (that is
  // what makes an in-app exit hand the seat back), so without this the watchdog's give-up path sent TWO
  // DELETEs for one lease — harmless to the server, but it puts a phantom second release into the lease-event
  // audit trail, which is the timeline we debug seat questions from.
  const releasedRef = useRef<Set<string>>(new Set());
  // Set when we re-attach to a slot whose UE is already in a venue. That UE is still holding the websocket
  // session of the page that is now gone — see the INIT effect, which has to move it onto this page's session.
  const staleUeSessionRef = useRef(false);
  // While the server is restarting the UE on our own seat there is deliberately no streamer, so "no frame
  // yet" is the expected state and must not be read as a dead slot — reclaiming here would hand away the very
  // seat the restart is preserving. How long that takes is not ours to guess: the signalling gateway
  // publishes the slot's live registration in every streamer list, so the wait is bound to that signal —
  // streamer absent = still coming up, streamer back = start judging it like any other entry.
  const ueRestartingRef = useRef(false);
  const ueRestartSawDropRef = useRef(false);
  const streamerPresentRef = useRef(false);
  const mediaRef = useRef<{ frames: number; at: number }>({ frames: 0, at: 0 });
  // One clock for the whole entry, so every line answers "how long since the user asked to enter".
  const entryT0Ref = useRef<number>(Date.now());
  const leaseAtRef = useRef<number>(0);
  const stepRef = useRef<string>('boot');
  const wsStateRef = useRef<string>('-');

  const mlog = useCallback((message: string) => {
    const t = ((Date.now() - entryT0Ref.current) / 1000).toFixed(1);
    MytaverseLogger.log(`MYTA_STREAMS [+${t}s] ${message}`);
  }, []);

  // Every line that describes progress carries the same identity block, because "it is stuck" is unusable
  // without knowing WHICH node and slot it is stuck on.
  const where = useCallback(
    () =>
      lease
        ? `node=${lease.nodeId} slot=${lease.slotIndex} streamer=${lease.streamerId} gw=${(() => {
            try {
              return new URL(lease.signallingUrl).host;
            } catch {
              return lease.signallingUrl;
            }
          })()}`
        : 'node=- slot=-',
    [lease],
  );

  const isPlayButtonPressedRef = useRef(false);
  const hoveringMouseIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const leaseRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const initRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const leaseIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      setPlayStream(false);
      setGameReadyToPlay(false);
      setConnectedSlot('');
      setInitMessageSended(false);
      setInitAttempt(0);
      setMytaPixelStreaming(null);
      if (hoveringMouseIntervalRef.current) {
        clearInterval(hoveringMouseIntervalRef.current);
        hoveringMouseIntervalRef.current = null;
      }
      if (initRetryTimeoutRef.current) {
        clearTimeout(initRetryTimeoutRef.current);
        initRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Mirrors the local instance onto the shared event context so
  // provider-agnostic UI (e.g. DevelopersTerminal) can reach it without
  // being mounted inside this provider's subtree.
  useEffect(() => {
    setMytaPixelStreaming(pixelStreaming);
  }, [pixelStreaming, setMytaPixelStreaming]);

  useEffect(() => {
    if (!enabled || !currentEvent) {
      return;
    }

    if (!MytaStreamsService.isLeaseApiConfigured()) {
      setLeaseStatus('unconfigured');
      setLeaseError(
        'MYTA lease API is not configured (REACT_APP_MYTA_STREAMS_LEASE_API_URL / REACT_APP_MYTA_STREAMS_LEASE_API_TOKEN)',
      );
      MytaverseLogger.log('MYTA_STREAMS lease API is not configured');
      return;
    }

    let cancelled = false;

    const adoptLease = (adopted: IMytaLease, source: 'lease' | 'rebind') => {
      leaseAtRef.current = Date.now();
      stepRef.current = 'lease-granted';
      mlog(
        `STEP lease ${source === 'rebind' ? 'REBOUND' : 'GRANTED'} ${adopted.leaseId} — node=${adopted.nodeId} slot=${adopted.slotIndex} streamer=${adopted.streamerId} url=${adopted.signallingUrl} build=${(adopted.buildUri ?? '').split('/').pop() ?? '-'}${adopted.sessionReady ? ` sessionReady=true map=${adopted.mapName ?? '-'} (UE already announced CLIENT_READY before this page existed — it will not repeat it)` : ''}`,
      );
      MytaStreamsService.saveStoredLease(currentEvent.eventId, adopted);
      // Reload rebind: settle the loader ourselves. The loader waits on `teleportFinished`, which
      // handleJoinedToEvent resets to false on every entry and which ONLY a TELEPORT_FINISHED can set —
      // but UE announces CLIENT_READY/TELEPORT_FINISHED exactly once, to whoever is attached at that
      // instant. An F5 attaches a NEW peer to a session that is already in the venue, so the announcement
      // is in the past and never repeats: measured 2026-08-11, 10/10 reload rounds sat on
      // "Loading your experience 95%" while frames decoded behind it at a clean 30 fps.
      // `sessionReady` is the lease API telling us exactly that (slot row's mapName is a real venue, not a
      // staging map), so it is the correct substitute for the event we will never receive.
      // Deliberately NOT applied to source === 'lease': a fresh grant boots UE, which will announce for
      // real, and short-circuiting there would drop the loader before the venue is up.
      if (adopted.restarting) {
        // The seat is ours and stays ours; the UE on it is being replaced so this page gets its own session.
        // Nothing to settle by hand — the fresh UE announces CLIENT_READY/JOINED_TO_ROOM for real, exactly
        // like a first entry, which is the whole point of paying for the restart.
        ueRestartingRef.current = true;
        ueRestartSawDropRef.current = false;
        streamerPresentRef.current = false;
        mlog(
          `STEP seat kept, UE RESTARTING on it (its session belonged to the page that disconnected) — waiting for streamer ${adopted.streamerId} to re-register${
            adopted.etaSec ? ` (server eta ~${adopted.etaSec}s)` : ''
          }`,
        );
      }

      if (source === 'rebind' && adopted.sessionReady) {
        staleUeSessionRef.current = true;
        setTeleportFinished(true);
        // …and put the room back. PixelStreamingWrapper renders the whole stream container only when
        // `currentRoom` is set, and `handleJoinedToEvent` wipes the participant's roomId on every entry —
        // it is restored by a JOINED_TO_ROOM that, like CLIENT_READY, was sent before this page existed.
        // So after an F5 the container is never mounted and the user gets a blank page even once the
        // loader is dismissed (measured live 2026-08-12: overlay gone, `mytaStreamsWrapper` absent while the
        // slot streamed the museum at 30 fps). The lease knows which venue the seat is in; the event provider
        // owns the rest, because `currentRoom` is derived from the participant and cannot be written directly.
        restoreSeatedRoom(adopted.mapName);
      }
      setLeaseStatus('active');
      setLeaseError(null);
      setLeasePhase(null);
      // Re-adopting the SAME leaseId (a dep-driven effect re-run rebinding in place) must not create
      // a new lease object: the [lease] effect's cleanup releases the OLD object's leaseId, and with
      // an unchanged id it would release the lease we just rebound — seat freed under a live stream.
      if (leaseIdRef.current === adopted.leaseId) {
        return;
      }
      leaseIdRef.current = adopted.leaseId;
      // The rebuild budget is per LEASE, not per page: a fresh slot deserves its own two attempts.
      rebuildsRef.current = 0;
      setLease(adopted);
      setTarget({
        signallingUrl: adopted.signallingUrl,
        streamerId: adopted.streamerId,
      });
    };

    // Reload rebind: an F5 lands here with the tab's lease still alive server-side (beforeunload no
    // longer releases it). Renew validates the seat is still ours; on success we reconnect to the SAME
    // slot in seconds — the UE process never restarted (watchdog counts 1 left / 1 joined as a
    // reconnect, strictly-greater rule). A failed renew (seat TTL-reclaimed or repurposed) just falls
    // through to a fresh lease.
    const tryRebind = async (): Promise<boolean> => {
      const stored = MytaStreamsService.loadStoredLease(currentEvent.eventId);
      if (!stored) {
        return false;
      }

      // The event's build may have been rebound while we were away — a stored lease for another
      // build must not be resumed (the user would land in the wrong world).
      if (
        eventBuildUri &&
        stored.buildUri &&
        stored.buildUri !== eventBuildUri
      ) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} is for another build — taking a fresh lease`,
        );
        return false;
      }

      const renewed = await MytaStreamsService.renewLease(stored.leaseId, {
        rebind: true,
      });
      if (cancelled) {
        return true;
      }
      if (renewed === 'network_error') {
        // A transient blip must not burn the stored lease — the fresh-lease path will run now, and
        // if it also fails the next retry can still rebind.
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} renew hit a network error — keeping it, trying a fresh lease`,
        );
        return false;
      }
      if (!renewed) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} not renewable — taking a fresh lease`,
        );
        return false;
      }

      adoptLease({ ...stored, ...renewed }, 'rebind');
      return true;
    };

    const requestLease = async () => {
      // Keep `waiting_slot` (and its phase card) up during retries — flipping back to `requesting`
      // per attempt made the loading HUD flash stage 1 and reset its countdown every 10-15 s.
      setLeaseStatus((prev) => (prev === 'waiting_slot' ? prev : 'requesting'));
      stepRef.current = 'lease-requesting';
      mlog(`STEP requesting a slot — event=${currentEvent.eventId} build=${(eventBuildUri ?? '').split('/').pop() ?? '-'}`);

      try {
        if (await tryRebind()) {
          return;
        }

        console.log('MYTA_TIMING t_acquireCall');
        const acquiredLease = await MytaStreamsService.acquireLease({
          eventId: currentEvent.eventId,
          userId: userId as string,
          buildUri: eventBuildUri,
        });

        if (cancelled) {
          MytaStreamsService.releaseLease(acquiredLease.leaseId);
          return;
        }

        adoptLease(acquiredLease, 'lease');
      } catch (e) {
        if (cancelled) {
          return;
        }

        let retryDelayMs = LEASE_RETRY_DELAY_MS;

        if (e instanceof NoFreeSlotError) {
          setLeaseStatus('waiting_slot');
          setLeaseError('No free slot available — retrying');
          setLeasePhase(e.details ?? null);
          if (e.details?.retryAfterSec) {
            retryDelayMs = Math.min(
              Math.max(e.details.retryAfterSec * 1000, LEASE_RETRY_MIN_DELAY_MS),
              LEASE_RETRY_MAX_DELAY_MS,
            );
          }
          // provisioning:false = the server is NOT working on capacity (unsupported build, pool
          // ceiling, another live session) — hammering it every 10 s is noise. Slow-poll so a
          // rebound build / freed node / closed other-tab still recovers without a reload.
          if (e.details?.provisioning === false) {
            retryDelayMs = Math.max(
              retryDelayMs,
              (e.details?.retryAfterSec ?? 60) * 1000,
            );
          }
          stepRef.current = `lease-wait:${e.details?.phase ?? '-'}`;
          mlog(
            `STEP server is provisioning — phase=${e.details?.phase ?? '-'} eta=${e.details?.etaSec ?? '-'}s provisioning=${
              e.details?.provisioning ?? '-'
            } node=${(e.details as { nodeId?: string })?.nodeId ?? '-'} next poll in ${Math.round(retryDelayMs / 1000)}s${
              e.details?.hint ? ` — ${e.details.hint}` : ''
            }`,
          );
        } else {
          setLeaseStatus('error');
          setLeaseError((e as Error).message);
          setLeasePhase(null);
          stepRef.current = 'lease-error';
          mlog(`STEP lease request FAILED: ${(e as Error).message} — retrying in ${Math.round(retryDelayMs / 1000)}s`);
        }

        leaseRetryTimeoutRef.current = setTimeout(requestLease, retryDelayMs);
      }
    };

    requestLease();

    return () => {
      cancelled = true;
      if (leaseRetryTimeoutRef.current) {
        clearTimeout(leaseRetryTimeoutRef.current);
        leaseRetryTimeoutRef.current = null;
      }
    };
  }, [enabled, currentEvent?.eventId, userId, eventBuildUri, leaseAttempt]);

  useEffect(() => {
    if (!lease) {
      return;
    }

    const renewInterval = setInterval(async () => {
      const renewed = await MytaStreamsService.renewLease(lease.leaseId);

      if (!renewed || renewed === 'network_error') {
        mlog(`RENEW ${renewed === 'network_error' ? 'network error' : 'REFUSED'} on ${lease.leaseId} — ${where()}`);
        return;
      }
      mlog(`RENEW ok ttl=${renewed.ttlSec ?? '-'}s step=${stepRef.current} frames=${mediaRef.current.frames} — ${where()}`);
    }, LEASE_RENEW_INTERVAL_MS);

    // Reload/close survival (BUG-2 2026-07-22): beforeunload must NOT release — an F5 that released
    // sent the slot through a restart cycle (60-90 s re-entry, and the ladder could start a whole GPU
    // node for the returning user). The stored lease lets the reloaded tab rebind to the same slot;
    // a genuinely closed tab is reclaimed by the server-side lease TTL (~120 s, T5-proven). Deliberate
    // in-app exits (event switch / back to the events list) unmount this provider and DO release.
    const leaseEventId = currentEvent?.eventId;
    const handleBeforeUnload = () => {
      if (leaseEventId) {
        MytaStreamsService.saveStoredLease(leaseEventId, lease);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(renewInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!releasedRef.current.has(lease.leaseId)) {
        MytaStreamsService.releaseLease(lease.leaseId, { keepalive: true });
      }
      if (leaseEventId) {
        MytaStreamsService.clearStoredLease(leaseEventId);
      }
      if (leaseIdRef.current === lease.leaseId) {
        leaseIdRef.current = null;
      }
    };
  }, [lease]);

  useEffect(() => {
    if (!target) {
      return;
    }

    Logger.InitLogging(LogLevel.Error, false);

    const config = new Config({
      useUrlParams: false,
      initialSettings: {
        AutoConnect: true,
        AutoPlayVideo: false,
        StartVideoMuted: false,
        HoveringMouse: true,
        TimeoutIfIdle: false,
        MatchViewportRes: true,
        MaxReconnectAttempts: 100,
        AFKTimeout: 3600,
        ForceTURN: true,
        WaitForStreamer: true,
        WebRTCMinBitrate: 8000,
        StreamerId: target.streamerId,
        ss: target.signallingUrl,
      },
    });

    stepRef.current = 'signalling-connecting';
    wsStateRef.current = 'connecting';
    mlog(
      `STEP connecting signalling (attempt ${rebuildsRef.current + 1}/${MAX_PEER_REBUILDS + 1} on this lease) — ${target.streamerId} @ ${target.signallingUrl} ForceTURN=true WaitForStreamer=true`,
    );

    const stream = new PixelStreaming(config);
    stream.videoElementParent.style.width = '100%';
    stream.videoElementParent.style.height = '100%';

    setPixelStreaming(stream);
    setRootElement(stream.videoElementParent);

    return () => {
      try {
        stream.disconnect();
      } catch (e) {
        console.warn('Failed to disconnect myta pixel streaming', e);
      }
    };
  }, [target, peerRebuild]);

  // KI-37 diagnostics. Every line here exists because its absence cost real debugging time: the failure looks
  // like silence, and silence was read as "the peer connection closed" for days. `streamerListMessage` is the
  // decisive one — it says whether the streamer we are waiting for is even registered.
  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    const onStreamerList = (e: Event) => {
      const d = (e as unknown as { data?: { messageStreamerList?: { ids?: string[] }; wantedStreamerId?: string; autoSelectedStreamerId?: string } }).data;
      const ids = d?.messageStreamerList?.ids ?? [];
      const wanted = d?.wantedStreamerId ?? '-';
      const present = ids.includes(wanted);
      // The live slot status the restart wait is driven by — the gateway republishes this list whenever a
      // streamer registers or drops, so it says when our UE is actually back without anyone timing it.
      streamerPresentRef.current = present;
      // "Back" only counts once we have seen it GO. The restart is dispatched asynchronously, so the first
      // streamer list after the rebind still lists the slot that is about to die — treating that as the
      // return ended the wait ~1 s in and handed the next 15 s to the ordinary first-frame budget, which the
      // restart then spent.
      if (!present && ueRestartingRef.current) {
        ueRestartSawDropRef.current = true;
      }
      if (present && ueRestartingRef.current && ueRestartSawDropRef.current) {
        ueRestartingRef.current = false;
        mediaRef.current = { frames: 0, at: Date.now() };
        rebuildsRef.current = 0;
        mlog(`STEP streamer ${wanted} is back after the UE restart — judging this entry like any other from here`);
      }
      stepRef.current = present ? 'streamer-found' : 'streamer-missing';
      // Receiving ANY message proves the gateway leg is fine, which is the distinction that matters:
      // `ws=connecting` in a heartbeat means we never heard from the gateway at all (network/ALB/TG), while
      // `ws=open` + `streamer-missing` is KI-37 proper — the slot's streamer is not registered.
      wsStateRef.current = 'open';
      // THE decisive line for KI-37: if our streamer is not in this list, the frontend will wait for it
      // silently and forever, and nothing else in the system can tell us that is what is happening.
      mlog(
        `STEP streamer list [${ids.join(',') || 'empty'}] wanted=${wanted} ${present ? 'PRESENT' : 'MISSING — the frontend will wait for it'} autoSelected=${
          d?.autoSelectedStreamerId ?? '-'
        }`,
      );
    };
    const onSubscribeFailed = (e: Event) => {
      stepRef.current = 'subscribe-failed';
      mlog(`STEP subscribe FAILED: ${String((e as unknown as { data?: { message?: string } }).data?.message ?? '')} — ${where()}`);
    };
    const onWebRtcConnecting = () => {
      wsStateRef.current = 'open';
      stepRef.current = 'webrtc-connecting';
      mlog(`STEP webRTC connecting — ${where()}`);
    };
    const onWebRtcFailed = () => {
      stepRef.current = 'webrtc-failed';
      mlog(`STEP webRTC FAILED — ${where()}`);
    };
    const onWebRtcDisconnected = (e: Event) => {
      stepRef.current = 'webrtc-disconnected';
      mlog(`STEP webRTC disconnected: ${String((e as unknown as { data?: { eventString?: string } }).data?.eventString ?? '')} — ${where()}`);
    };
    const onDataChannelClose = () => {
      stepRef.current = 'data-channel-closed';
      mlog(`STEP data channel CLOSED — ${where()}`);
    };
    const onPlayerCount = (e: Event) => {
      mlog(`STEP streamer reports ${String((e as unknown as { data?: { count?: number } }).data?.count ?? '?')} player(s) on this slot — ${where()}`);
    };

    pixelStreaming.addEventListener('webRtcConnecting', onWebRtcConnecting);
    pixelStreaming.addEventListener('playerCount', onPlayerCount);
    pixelStreaming.addEventListener('streamerListMessage', onStreamerList);
    pixelStreaming.addEventListener('subscribeFailed', onSubscribeFailed);
    pixelStreaming.addEventListener('webRtcFailed', onWebRtcFailed);
    pixelStreaming.addEventListener('webRtcDisconnected', onWebRtcDisconnected);
    pixelStreaming.addEventListener('dataChannelClose', onDataChannelClose);

    return () => {
      pixelStreaming.removeEventListener('webRtcConnecting', onWebRtcConnecting);
      pixelStreaming.removeEventListener('playerCount', onPlayerCount);
      pixelStreaming.removeEventListener('streamerListMessage', onStreamerList);
      pixelStreaming.removeEventListener('subscribeFailed', onSubscribeFailed);
      pixelStreaming.removeEventListener('webRtcFailed', onWebRtcFailed);
      pixelStreaming.removeEventListener('webRtcDisconnected', onWebRtcDisconnected);
      pixelStreaming.removeEventListener('dataChannelClose', onDataChannelClose);
    };
  }, [pixelStreaming, mlog, where]);

  // KI-37 watchdog: the media path is the only thing that proves this session works, so it is the only thing
  // worth watching. Lease renewals prove nothing (the server's view was correct in every recorded case).
  useEffect(() => {
    if (!pixelStreaming || !target || !lease) {
      return;
    }

    const startedAt = Date.now();
    mediaRef.current = { frames: 0, at: startedAt };

    let framedLogged = false;
    const onStats = (e: Event) => {
      const stats = (e as unknown as {
        data?: {
          aggregatedStats?: {
            inboundVideoStats?: { framesDecoded?: number; framesReceived?: number; frameWidth?: number; frameHeight?: number; framesPerSecond?: number; bitrate?: number };
          };
        };
      }).data;
      const inbound = stats?.aggregatedStats?.inboundVideoStats;
      const decoded = Number(inbound?.framesDecoded ?? inbound?.framesReceived ?? 0);
      if (decoded > mediaRef.current.frames) {
        mediaRef.current = { frames: decoded, at: Date.now() };
        if (!framedLogged) {
          framedLogged = true;
          stepRef.current = 'streaming';
          const sinceLease = leaseAtRef.current ? `${Math.round((Date.now() - leaseAtRef.current) / 100) / 10}s after the lease` : 'timing unknown';
          mlog(
            `STEP FIRST FRAME decoded (${inbound?.frameWidth ?? '?'}x${inbound?.frameHeight ?? '?'}) — ${sinceLease} — ${where()}`,
          );
        }
      }
    };
    pixelStreaming.addEventListener('statsReceived', onStats);

    // Live view while the entry is in flight: which step, how long in it, what the media path has produced.
    // Silent once the stream runs — the stall branch below is what watches a running session.
    const heartbeat = setInterval(() => {
      if (mediaRef.current.frames > 0) {
        return;
      }
      mlog(
        `WAITING step=${stepRef.current} ws=${wsStateRef.current} dc=${dataChannelOpened ? 'open' : 'closed'} frames=0 attempt=${
          rebuildsRef.current + 1
        }/${MAX_PEER_REBUILDS + 1} — ${where()}`,
      );
    }, ENTRY_HEARTBEAT_MS);

    const giveUpSlot = async () => {
      const dead = lease.leaseId;
      mlog(`WATCHDOG releasing lease ${dead} after ${MAX_PEER_REBUILDS} failed rebuilds — asking the ladder for another slot`);
      // Release explicitly and BEFORE asking for a new lease: the [lease] cleanup would also release, but the
      // fresh request must not race the old seat still being held (the ladder would see no free slot and, at
      // the ceiling, answer capacity_wait for a seat we are about to hand back).
      if (currentEvent?.eventId) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
      }
      try {
        releasedRef.current.add(dead);
        await MytaStreamsService.releaseLease(dead);
      } catch (e) {
        console.warn('Failed to release a dead myta lease', e);
      }
      rebuildsRef.current = 0;
      leaseIdRef.current = null;
      setDataChannelOpened(false);
      setPlayStream(false);
      setInitMessageSended(false);
      setTarget(null);
      setLease(null);
      setLeaseStatus('requesting');
      setLeaseAttempt((n) => n + 1);
    };

    const timer = setInterval(() => {
      // A hidden tab is throttled by the browser: frames legitimately stop, and rebuilding then would break a
      // session that is merely in the background.
      if (typeof document !== 'undefined' && document.hidden) {
        mediaRef.current = { ...mediaRef.current, at: Date.now() };
        return;
      }

      // A restart the server told us about is not a stall. Waiting on the live signal rather than a timer:
      // as long as our streamer is not in the gateway's list, the UE is still coming up and there is nothing
      // for the watchdog to judge. Reclaiming here would spend the seat the restart exists to keep.
      if (ueRestartingRef.current && !streamerPresentRef.current) {
        mediaRef.current = { ...mediaRef.current, at: Date.now() };
        return;
      }

      // Measured from `at`, which starts at the effect's own start and is re-stamped whenever the clock must
      // not run — a hidden tab, or a UE restart we were told to wait through. Using the effect start directly
      // would declare a slot dead the instant its streamer came back from a restart we had just waited out.
      const { frames, at } = mediaRef.current;
      const idleMs = Date.now() - at;
      const stuck = frames > 0 ? idleMs > MEDIA_STALL_TIMEOUT_MS : idleMs > FIRST_FRAME_TIMEOUT_MS;
      if (!stuck) {
        return;
      }

      const what = frames > 0 ? `stream stalled at ${frames} frames for ${Math.round(idleMs / 1000)}s` : `no decoded frame in ${Math.round(idleMs / 1000)}s`;

      if (rebuildsRef.current < MAX_PEER_REBUILDS) {
        rebuildsRef.current += 1;
        mlog(`WATCHDOG ${what} — rebuilding the peer on the SAME lease (rebuild ${rebuildsRef.current}/${MAX_PEER_REBUILDS}) — ${where()}`);
        mediaRef.current = { frames: 0, at: Date.now() };
        setDataChannelOpened(false);
        setInitMessageSended(false);
        setPeerRebuild((n) => n + 1);
        return;
      }

      mlog(`WATCHDOG ${what} — rebuild budget spent, handing the slot back — ${where()}`);
      void giveUpSlot();
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(heartbeat);
      pixelStreaming.removeEventListener('statsReceived', onStats);
    };
  }, [pixelStreaming, target, lease, peerRebuild, currentEvent?.eventId, setInitMessageSended, dataChannelOpened, mlog, where]);

  const handleDataChannelOpened = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    stepRef.current = 'data-channel-open';
    mlog(`STEP data channel OPEN — ${where()}`);

    if (!hoveringMouseIntervalRef.current) {
      hoveringMouseIntervalRef.current = setInterval(() => {
        pixelStreaming.config.setSettings({
          HoveringMouse: true,
        });
      }, 1000);
    }

    setDataChannelOpened(true);
  }, [pixelStreaming]);

  const handleWebRtcConnected = useCallback(async () => {
    stepRef.current = 'webrtc-connected';
    wsStateRef.current = 'open';
    mlog(`STEP webRTC CONNECTED — ${where()}`);
    setInitMessageSended(false);
    setDataChannelOpened(false);
    setConnectedSlot(target?.streamerId || '');

    if (!userId || !currentEvent) {
      return;
    }

    try {
      await PixelStreamingLogsService.createPixelStreamingLog({
        participantId: userId,
        eventId: currentEvent.eventId,
        timestamp: Date.now(),
        pixelStreamingProvider: 'MYTA_STREAMS',
        pixelStreamingProviderData: {
          id: target?.streamerId || '',
        },
      });
    } catch (e) {
      console.warn('Failed to create myta pixel streaming log', e);
    }
  }, [userId, currentEvent, target, setInitMessageSended]);

  const handleInitializeButton = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    const video = pixelStreaming.videoElementParent.querySelector('video');
    if (video) {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'contain';
    }

    stepRef.current = 'video-initialized';
    mlog(`STEP video track INITIALIZED — ${where()}`);
    setMytaLoading(false);
  }, [pixelStreaming, mlog, where]);

  const handlePlayStream = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    if (env.REACT_APP_MYTAVERSE_ENV !== 'PROD') {
      (pixelStreaming as any).requestShowFps?.();
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    stepRef.current = 'playing';
    mlog(`STEP playStream — video is playing, ${Math.round((Date.now() - (leaseAtRef.current || Date.now())) / 100) / 10}s after the lease — ${where()}`);
    setPlayStream(true);
    setGameReadyToPlay(true);

    if (!isPlayButtonPressedRef.current) {
      isPlayButtonPressedRef.current = true;
    }
  }, [pixelStreaming, setGameReadyToPlay]);

  const handleSettingChanged = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    if (!pixelStreaming.config.getSettings().HoveringMouse) {
      pixelStreaming.config.setSettings({
        HoveringMouse: true,
      });
    }
  }, [pixelStreaming]);

  const handleResize = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });
  }, [pixelStreaming]);

  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.addEventListener('videoInitialized', handleInitializeButton);
    pixelStreaming.addEventListener('settingsChanged', handleSettingChanged);
    pixelStreaming.addEventListener('dataChannelOpen', handleDataChannelOpened);
    pixelStreaming.addEventListener('playStream', handlePlayStream);
    pixelStreaming.addEventListener('webRtcConnected', handleWebRtcConnected);
    window.addEventListener('resize', handleResize);

    return () => {
      pixelStreaming.removeEventListener(
        'videoInitialized',
        handleInitializeButton,
      );
      pixelStreaming.removeEventListener(
        'settingsChanged',
        handleSettingChanged,
      );
      pixelStreaming.removeEventListener(
        'dataChannelOpen',
        handleDataChannelOpened,
      );
      pixelStreaming.removeEventListener('playStream', handlePlayStream);
      pixelStreaming.removeEventListener(
        'webRtcConnected',
        handleWebRtcConnected,
      );
      window.removeEventListener('resize', handleResize);
    };
  }, [
    pixelStreaming,
    handleInitializeButton,
    handleSettingChanged,
    handleDataChannelOpened,
    handlePlayStream,
    handleWebRtcConnected,
    handleResize,
  ]);

  // Reload race, 2026-08-11: LoadingCinema is gated on `playStream`, and `playStream` is only ever set from
  // the library's one-shot `playStream` event. On a reload REBIND the lease, the streamer and the slot all
  // pre-exist, so the video can start playing BEFORE the listener effect above commits — the event lands in
  // the past, nothing sets `playStream`, and the loader covers a stream that is already decoding. Measured
  // live: `FIRST FRAME decoded 2.8s after the lease` with no `STEP playStream` line at all, loader forever.
  // A listener cannot see the past, so ask the video element instead. Same class as the harness's
  // ensureInitSent race. Bounded poll because the element is attached a beat after the instance appears.
  // Deliberately NOT gated on `playStream`: the element can be replaced after that latched (a peer rebuild
  // brings its own videoElementParent) and the replacement arrives paused at currentTime 0, with no user
  // gesture to autoplay it. Measured 2026-08-12 on reload round 9 of 10 — srcObject set, readyState 4,
  // paused=true, t=0, loader already gone: a frozen picture and no way for the user to notice why. So keep a
  // cheap resumer alive for the session instead of only until the first play.
  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    // Pick the element that actually carries the stream. Never widen this to the document: `rootElement`
    // is deliberately NOT in the DOM until playStream flips (PixelStreamingWrapper mounts it only then),
    // and a document-wide query returns OTHER providers' tags (e.g. GameCast's `gamecastVideoTag`) — a
    // different provider entirely, whose element will never carry a MYTA_STREAMS stream.
    const liveVideo = (): HTMLVideoElement | null =>
      [...(rootElement?.querySelectorAll('video') ?? [])].find(
        (v) => !!v.srcObject && v.readyState >= 2,
      ) ?? null;

    const adoptIfAlreadyPlaying = () => {
      const video = liveVideo();
      if (!video) {
        return false;
      }
      if (video.paused || !video.currentTime) {
        // Measured live in Chrome 2026-08-12 on a reload rebind: srcObject set, readyState 4 (enough data),
        // paused=true, currentTime=0 — the stream is attached and buffered, nobody ever started it. A page
        // load after F5 carries no user activation, so the library's own play() is refused by autoplay
        // policy, its one-shot `playStream` never fires, and the loader waits on an event that cannot come.
        // Muted playback is always permitted, so prefer muted-and-visible over black-forever; the click the
        // user is about to make in-world restores sound.
        void video.play().catch(() => {
          video.muted = true;
          return video.play().catch(() => undefined);
        });
        return false;
      }
      if (!playStream) {
        mlog(
          'STEP playStream ADOPTED — video was already playing before the listeners attached (reload rebind); the one-shot event was missed',
        );
        handlePlayStream();
      }
      return true;
    };

    adoptIfAlreadyPlaying();

    const timer = window.setInterval(adoptIfAlreadyPlaying, 500);

    return () => window.clearInterval(timer);
  }, [pixelStreaming, rootElement, playStream, handlePlayStream]);

  useEffect(() => {
    if (dataChannelOpened && !isPlayButtonPressedRef.current) {
      setGameReadyToPlay(false);
    }
  }, [dataChannelOpened, setGameReadyToPlay]);

  useAsyncEffect(async () => {
    if (
      !initMessageHandler ||
      !pixelStreaming ||
      !dataChannelOpened ||
      initMessageSended
    ) {
      return;
    }

    if (initRetryTimeoutRef.current) {
      clearTimeout(initRetryTimeoutRef.current);
      initRetryTimeoutRef.current = null;
    }

    const message = await initMessageHandler();

    if (!message) {
      if (initAttempt >= 15) {
        stepRef.current = 'init-unavailable';
        mlog(
          `STEP INIT payload still unavailable after ${
            initAttempt + 1
          } attempts — giving up (player-session mint keeps failing) — ${where()}`,
        );
        return;
      }

      stepRef.current = 'init-retry';
      mlog(
        `STEP INIT payload unavailable (player-session mint empty or failed, attempt ${
          initAttempt + 1
        }) — retry in 6s — ${where()}`,
      );
      initRetryTimeoutRef.current = setTimeout(
        () => setInitAttempt((attempt) => attempt + 1),
        6000,
      );

      return;
    }

    pixelStreaming.emitUIInteraction(message);

    setTimeout(() => {
      pixelStreaming.emitUIInteraction(message);
    }, 1000);

    // KNOWN GAP, measured 2026-08-12: a UE that is already logged in keeps the websocket session it was
    // INIT-ed with, and neither a second INIT nor a WS_CONNECTION_URL over this data channel moves it (both
    // tried; the slot kept reconnecting as the previous page's session a minute later). So after a reload the
    // stream is correct while UE's control link still belongs to a page that no longer exists. Re-pointing it
    // has to come from the UE side; until then the entry is carried by the seated-room restore in
    // MytaverseEventProvider, and anything that rides that link (chat, teleport, participant presence) stays
    // bound to the old session.
    if (staleUeSessionRef.current) {
      staleUeSessionRef.current = false;
      mlog(
        `STEP re-INIT sent to a slot that was already logged in — UE keeps its previous websocket session (known UE-side gap) — ${where()}`,
      );
    }

    setInitMessageSended(true);

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    stepRef.current = 'init-sent';
    mlog(`STEP INIT sent to UE (login payload) — ${where()}`);
    MytaverseLogger.log(`INIT ${JSON.stringify(message)}`);
  }, [
    initMessageSended,
    pixelStreaming,
    dataChannelOpened,
    initMessageHandler,
    initAttempt,
  ]);

  useEffect(() => {
    if (!dataChannelOpened || !pixelStreaming) {
      return;
    }

    const message = {
      action: 'CHANGE_WORLD_SOUND',
      value: gameSound,
    };

    pixelStreaming.emitUIInteraction(JSON.stringify(message));
  }, [gameSound, dataChannelOpened, pixelStreaming]);

  const togglePlayStream = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.play();
  }, [pixelStreaming]);

  return {
    pixelStreaming,
    mytaLoading,
    dataChannelOpened,
    playStream,
    togglePlayStream,
    isPlayButtonPressed: isPlayButtonPressedRef.current,
    rootElement,
    connectedSlot,
    lease,
    leaseStatus,
    leaseError,
    leasePhase,
  };
};

export const useMytaStreams = (): IMytaStreamsContext => {
  return useMytaverseEvent().mytaStreams;
};
